import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import type { AgentSession, SessionCreated, SessionsList } from '@one-deployment/api'
import { enc, get, post } from '@/lib/api'
import { qk } from '@/lib/query'

/**
 * Every open tmux session in a project's container: the robot's ticket
 * sessions and people's own. A person's session is permanent — it runs until
 * someone closes it here. Polled: a new session shows up within a few seconds.
 */
export function useProjectSessions(project: string) {
  return useQuery({
    queryKey: qk.projectSessions(project),
    queryFn: ({ signal }) => get<SessionsList>(`/api/projects/${enc(project)}/sessions`, signal),
    refetchInterval: 4_000,
  })
}

export function sessionLabel(s: AgentSession): string {
  return s.ticket ? `${s.ticket} · ${s.task}` : s.label ?? s.tmuxSession.replace(/^[^-]+-s-/, '')
}

export function sortSessions(list: AgentSession[]): AgentSession[] {
  return [...list].sort((a, b) => Number(!!a.jobId) - Number(!!b.jobId) || (a.jobId ? b.startedAt - a.startedAt : a.startedAt - b.startedAt))
}

/** An open session's page: /p/<project>/s/<id>. */
export const isSessionPath = (path: string) => /^\/p\/[^/]+\/s\/[^/]+/.test(path)

/** Starts a session and opens it as soon as the box lists it. */
export function useCreateSession(project: string) {
  const qc = useQueryClient()
  const navigate = useNavigate()
  return useMutation({
    mutationFn: () => post<SessionCreated>(`/api/projects/${enc(project)}/sessions`, {}),
    onSuccess: async (r) => {
      // The box starts it in a second or two; open it as soon as it is listed.
      for (let i = 0; i < 15; i++) {
        const list = await qc.fetchQuery({ queryKey: qk.projectSessions(project), queryFn: () => get<SessionsList>(`/api/projects/${enc(project)}/sessions`), staleTime: 0 })
        const s = list.sessions.find((x) => x.tmuxSession === r.tmuxSession)
        if (s) { void navigate({ to: '/p/$project/s/$session', params: { project, session: s.id } }); return }
        await new Promise((ok) => setTimeout(ok, 1_000))
      }
      throw new Error('The session did not start. Check that the project container is running.')
    },
  })
}

/** Sessions as the switcher and the list show them: push to prod's own session is opened from its progress window, not listed. */
export function listedSessions(all: AgentSession[], sid?: string) {
  return sortSessions(all).filter((s) => !/-ship-[a-z0-9]+$/.test(s.tmuxSession) || s.id === sid)
}
